import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { Button, Modal, Badge, Alert, Form } from 'react-bootstrap';
import api from "../../services/api";
import { userMessageOf } from '../../services/apiError';
import { nameOf } from './userLoginsList.logic';
import Loading from '../Shared/Loading';
import TableFull from '../Shared/CommonTable/TableFull';
import { toast } from 'react-toastify';
import { dateAsNumbersWithoutSeconds } from '../../utils/dateFormatter';
import styles from './PushTargetsManagement.module.scss';

// Mirrors DPushNotification.BodyMaxLength; the server is authoritative
const BODY_MAX_LENGTH = 1000;

export default function PushTargetsManagement() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const loginUidFilter = searchParams.get('loginUid');

  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [items, setItems] = useState([]);
  const [processing, setProcessing] = useState(false);

  const [emailFilter, setEmailFilter] = useState('');
  const [platformFilter, setPlatformFilter] = useState('');
  const [appTypeFilter, setAppTypeFilter] = useState('');

  const [sendTarget, setSendTarget] = useState(null);
  const [sendBody, setSendBody] = useState('');
  const [loginFilterLogin, setLoginFilterLogin] = useState(null);

  useEffect(() => {
    loadList();
  }, []);

  // The filtered login is named from the login itself: it may have no phones at all, and then no row carries its email
  useEffect(() => {
    if (!loginUidFilter) {
      setLoginFilterLogin(null);
      return;
    }

    let current = true;
    api.get(`/settings/admin/user-login/${loginUidFilter}`)
      .then(response => { if (current) setLoginFilterLogin(response.data); })
      .catch(error => {
        if (current) setLoginFilterLogin(null);
        console.error('Error loading the filtered login, status:', error?.response?.status);
      });
    return () => { current = false; };
  }, [loginUidFilter]);

  const loadList = async () => {
    setLoading(true);
    try {
      const response = await api.get('/settings/admin/push-targets');
      setItems(response.data.items);
      setLoadFailed(false);
    } catch (error) {
      setLoadFailed(true);
      toast.error(t('admin.pushTargets.loadError'));
      console.error('Error loading push targets:', error);
    } finally {
      setLoading(false);
    }
  };

  // Built from the current params: react-router 6.2 has no functional setSearchParams, a callback there wipes the query
  const dropSearchParams = (...names) => {
    const next = new URLSearchParams(searchParams);
    names.forEach(name => next.delete(name));
    setSearchParams(next, { replace: true });
  };

  // TableFull keeps its page in ?page= and never resets it, so a filter applied on a later page would show nothing
  const resetPage = () => dropSearchParams('page');

  const clearLoginFilter = () => dropSearchParams('loginUid', 'page');

  const openSendModal = (item) => {
    setSendBody('');
    setSendTarget(item);
  };

  const canSubmitSend = sendBody.trim().length > 0 && sendBody.trim().length <= BODY_MAX_LENGTH;

  const submitSend = async () => {
    if (!sendTarget) return;

    setProcessing(true);
    try {
      // The login goes along: a phone can move to another login and keep its uid, and the server then refuses
      await api.post(`/settings/admin/push-targets/${sendTarget.uid}/send-test`,
        { userLoginUid: sendTarget.userLoginUid, body: sendBody.trim() });
      // Queued is all the web app knows: the function app sends, and accepted by FCM is not delivered
      toast.success(t('admin.pushTargets.sendQueued'));
      setSendTarget(null);
    } catch (error) {
      toast.error(userMessageOf(error) || t('admin.pushTargets.sendError'));
      // Status only: the axios error object carries the request body, which is the text just typed
      console.error('Error queueing test push, status:', error?.response?.status);
    } finally {
      setProcessing(false);
    }
  };

  // The platform travels as the enum's int; its name is a translation keyed by that int
  const platforms = useMemo(
    () => [...new Set(items.map(x => x.platform))].sort((a, b) => a - b),
    [items]
  );

  // The app type (Guard / Plaster) travels the same way; 0 is a phone registered before the app type existed
  const appTypes = useMemo(
    () => [...new Set(items.map(x => x.appType))].sort((a, b) => a - b),
    [items]
  );

  const filteredItems = useMemo(() => {
    const email = emailFilter.trim().toLowerCase();
    return items.filter(x =>
      (!loginUidFilter || x.userLoginUid === loginUidFilter) &&
      (!email || (x.userLoginEmail || '').toLowerCase().includes(email)) &&
      (!platformFilter || String(x.platform) === platformFilter) &&
      (!appTypeFilter || String(x.appType) === appTypeFilter));
  }, [items, loginUidFilter, emailFilter, platformFilter, appTypeFilter]);

  // Name and email where the login is known, else whatever a phone row carries, else the uid from the link
  const loginFilterLabel = useMemo(() => {
    if (!loginUidFilter) return '';

    const email = loginFilterLogin?.email || items.find(x => x.userLoginUid === loginUidFilter)?.userLoginEmail;
    const name = loginFilterLogin ? nameOf(loginFilterLogin) : '';
    if (name && email) return `${name} (${email})`;
    return name || email || loginUidFilter;
  }, [loginFilterLogin, items, loginUidFilter]);

  const columns = useMemo(() => [
    {
      header: t('admin.pushTargets.login'),
      accessorKey: 'userLoginEmail',
      cell: ({ row }) => {
        const item = row.original;
        if (!item.userLoginUid) {
          return <Badge bg="danger">{t('admin.pushTargets.loginMissing')}</Badge>;
        }
        return (
          <>
            {item.userLoginEmail}{' '}
            {!item.isUserLoginActive && <Badge bg="secondary">{t('admin.pushTargets.loginInactive')}</Badge>}
          </>
        );
      },
    },
    {
      header: t('admin.pushTargets.platform'),
      accessorKey: 'platform',
      cell: info => t(`admin.pushTargets.platformNames.${info.getValue()}`),
    },
    {
      header: t('admin.pushTargets.appType'),
      accessorKey: 'appType',
      cell: info => t(`admin.pushTargets.appTypeNames.${info.getValue()}`),
    },
    {
      header: t('admin.pushTargets.deviceModel'),
      accessorKey: 'deviceModel',
      cell: info => info.getValue() || '-',
    },
    {
      header: t('admin.pushTargets.appVersion'),
      accessorKey: 'appVersion',
      cell: info => info.getValue() || '-',
    },
    {
      header: t('admin.pushTargets.lastSeen'),
      accessorKey: 'lastSeenUtc',
      cell: info => dateAsNumbersWithoutSeconds(info.getValue()),
    },
    {
      header: t('admin.pushTargets.registered'),
      accessorKey: 'dateCreatedUtc',
      cell: info => dateAsNumbersWithoutSeconds(info.getValue()),
    },
    {
      header: t('admin.pushTargets.actions'),
      id: 'actions',
      cell: ({ row }) => (
        <div className={styles.actionsCell} onClick={(e) => e.stopPropagation()}>
          {/* By uid: only uids reach the browser. A removed phone is not on this page, and its history stays under its login */}
          <Link className="btn btn-outline-secondary btn-sm" to={`/admin/push-notifications?targetUid=${row.original.uid}`}>
            {t('admin.pushTargets.messages')}
          </Link>
          <Button
            variant="outline-primary"
            size="sm"
            disabled={processing || !row.original.userLoginUid}
            onClick={() => openSendModal(row.original)}
          >
            {t('admin.pushTargets.sendButton')}
          </Button>
        </div>
      ),
    },
  ], [t, processing]);

  if (loading) {
    return <Loading />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.filters}>
          <Form.Control
            type="text"
            value={emailFilter}
            onChange={e => { setEmailFilter(e.target.value); resetPage(); }}
            placeholder={t('admin.pushTargets.emailFilterPlaceholder')}
          />
          <Form.Select value={platformFilter} onChange={e => { setPlatformFilter(e.target.value); resetPage(); }}>
            <option value="">{t('admin.pushTargets.allPlatforms')}</option>
            {platforms.map(p => <option key={p} value={p}>{t(`admin.pushTargets.platformNames.${p}`)}</option>)}
          </Form.Select>
          <Form.Select value={appTypeFilter} onChange={e => { setAppTypeFilter(e.target.value); resetPage(); }}>
            <option value="">{t('admin.pushTargets.allAppTypes')}</option>
            {appTypes.map(a => <option key={a} value={a}>{t(`admin.pushTargets.appTypeNames.${a}`)}</option>)}
          </Form.Select>
        </div>
        <div className={styles.headerActions}>
          <Button variant="outline-primary" onClick={loadList}>
            {t('admin.pushTargets.refresh')}
          </Button>
        </div>
      </div>

      {loginUidFilter && (
        <Alert variant="info" dismissible onClose={clearLoginFilter}>
          <Trans
            i18nKey="admin.pushTargets.filteredByLogin"
            values={{ login: loginFilterLabel }}
            components={[<strong key="login" />]}
          />
        </Alert>
      )}

      {filteredItems.length === 0 ? (
        <div className={styles.emptyState}>
          {/* A failed load must not present itself as an empty list */}
          <Alert variant={loadFailed ? 'danger' : 'info'}>
            {loadFailed ? t('admin.pushTargets.loadError') : t('admin.pushTargets.noTargets')}
          </Alert>
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          <TableFull
            data={filteredItems}
            columns={columns}
            styles={styles}
            allowColumnHeaderSort={true}
            mainTableClassName={styles.table}
          />
        </div>
      )}

      <Modal show={!!sendTarget} onHide={() => setSendTarget(null)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('admin.pushTargets.sendTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {sendTarget && (
            <p>
              <strong>{sendTarget.userLoginEmail}</strong>{' '}
              {[
                t(`admin.pushTargets.platformNames.${sendTarget.platform}`),
                t(`admin.pushTargets.appTypeNames.${sendTarget.appType}`),
                sendTarget.deviceModel,
              ].filter(Boolean).join(' · ')}
            </p>
          )}
          <Form.Group className="mb-3">
            <Form.Label>{t('admin.pushTargets.sendBodyLabel')}</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              maxLength={BODY_MAX_LENGTH}
              value={sendBody}
              onChange={e => setSendBody(e.target.value)}
            />
          </Form.Group>
          {/* The text is shown on a lock screen */}
          <Alert variant="warning" className="mb-0">
            {t('admin.pushTargets.sendNoPersonalData')}
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSendTarget(null)}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={submitSend} disabled={!canSubmitSend || processing}>
            {processing && (<span className="spinner-border spinner-border-sm me-1"></span>)}
            {t('admin.pushTargets.sendButton')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
